import { useState, useEffect, useCallback, useRef } from "react"
import { router } from "@inertiajs/react"
import { OptimizedImage } from "@/components/ui/optimized-image"

interface ImageItem {
  signed_id: string
  url: string
  description?: string
}

interface ImageLightboxProps {
  images: ImageItem[]
  initialIndex: number
  listingSlug?: string
  canDelete?: boolean
  /** Callback mode: called with signed_id when removing an image (e.g. edit form). */
  onDelete?: (signedId: string) => void
  onClose: () => void
}

export default function ImageLightbox({
  images,
  initialIndex,
  listingSlug,
  canDelete = false,
  onDelete,
  onClose,
}: ImageLightboxProps) {
  const [index, setIndex] = useState(initialIndex)
  const [dimensions, setDimensions] = useState<{ w: number; h: number } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const filmstripRef = useRef<HTMLDivElement>(null)
  const activeThumbRef = useRef<HTMLButtonElement>(null)

  const safeIndex = Math.min(index, Math.max(images.length - 1, 0))
  const current = images[safeIndex] as ImageItem | undefined
  const hasPrev = safeIndex > 0
  const hasNext = safeIndex < images.length - 1

  const goPrev = useCallback(() => {
    if (safeIndex > 0) {
      setIndex(safeIndex - 1)
      setConfirmDelete(false)
      setImageLoaded(false)
    }
  }, [safeIndex])

  const goNext = useCallback(() => {
    if (safeIndex < images.length - 1) {
      setIndex(safeIndex + 1)
      setConfirmDelete(false)
      setImageLoaded(false)
    }
  }, [safeIndex, images.length])

  const goTo = useCallback((i: number) => {
    setIndex(i)
    setConfirmDelete(false)
    setImageLoaded(false)
  }, [])

  const handleDelete = useCallback(() => {
    if (!current) return

    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }

    const signedId = current.signed_id

    // If deleting last image, move index back
    if (safeIndex >= images.length - 1 && safeIndex > 0) {
      setIndex(safeIndex - 1)
    }

    if (onDelete) {
      // Callback mode (edit form) — synchronous removal via parent state
      onDelete(signedId)
      setConfirmDelete(false)
    } else if (listingSlug) {
      // API mode (show page) — delete via server
      setDeleting(true)
      router.delete(
        `/dashboard/listings/${listingSlug}/images/${encodeURIComponent(signedId)}`,
        {
          preserveState: true,
          preserveScroll: true,
          onFinish: () => {
            setDeleting(false)
            setConfirmDelete(false)
          },
        },
      )
    }
  }, [confirmDelete, current, safeIndex, images.length, listingSlug, onDelete])

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (confirmDelete) {
          setConfirmDelete(false)
        } else {
          onClose()
        }
      } else if (e.key === "ArrowLeft") {
        goPrev()
      } else if (e.key === "ArrowRight") {
        goNext()
      }
    }
    document.addEventListener("keydown", handleKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", handleKey)
      document.body.style.overflow = ""
    }
  }, [onClose, goPrev, goNext, confirmDelete])

  // Scroll active thumbnail into view
  useEffect(() => {
    activeThumbRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    })
  }, [safeIndex])

  // Load image dimensions
  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget
      setDimensions({ w: img.naturalWidth, h: img.naturalHeight })
      setImageLoaded(true)
    },
    [],
  )

  // Close if no images remain
  useEffect(() => {
    if (images.length === 0) onClose()
  }, [images.length, onClose])

  if (!current) return null

  const isLowRes = dimensions && (dimensions.w < 1000 || dimensions.h < 700)

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-charcoal/[.97]">
      {/* Top bar */}
      <div className="flex shrink-0 items-center justify-between px-5 py-3.5">
        {/* Left: counter + dimensions */}
        <div className="flex items-center gap-4">
          <span className="tabular-nums text-sm font-medium tracking-wide text-cream/50">
            {safeIndex + 1}
            <span className="mx-1 text-cream/25">/</span>
            {images.length}
          </span>
          {canDelete && dimensions && imageLoaded && (
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium tabular-nums tracking-wide ${
                isLowRes
                  ? "bg-red/20 text-red"
                  : "bg-white/5 text-cream/40"
              }`}
            >
              {dimensions.w} &times; {dimensions.h}
              {isLowRes && " — low res"}
            </span>
          )}
        </div>

        {/* Right: delete + close */}
        <div className="flex items-center gap-1">
          {canDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-150 ${
                confirmDelete
                  ? "bg-red text-white"
                  : "text-cream/50 hover:bg-white/10 hover:text-cream"
              } ${deleting ? "pointer-events-none opacity-40" : ""}`}
            >
              {deleting ? (
                <svg
                  className="size-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="3"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : (
                <svg
                  className="size-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                  />
                </svg>
              )}
              {confirmDelete ? "Confirm" : onDelete ? "Remove" : "Delete"}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex size-9 items-center justify-center rounded-lg text-cream/50 transition-colors hover:bg-white/10 hover:text-cream"
          >
            <svg
              className="size-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-16">
        {/* Previous */}
        {hasPrev && (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-3 flex size-11 items-center justify-center rounded-full text-cream/40 transition-colors hover:bg-white/10 hover:text-cream"
          >
            <svg
              className="size-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5 8.25 12l7.5-7.5"
              />
            </svg>
          </button>
        )}

        {/* Image + description */}
        <div className="flex max-h-full flex-col items-center">
          <img
            key={current.url}
            src={current.url}
            alt={`Photo ${safeIndex + 1}`}
            onLoad={handleImageLoad}
            className={`max-h-full max-w-full rounded object-contain transition-opacity duration-300 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
          />
          {current.description && imageLoaded && (
            <p className="mt-2 max-w-lg text-center text-sm text-cream/60">
              {current.description}
            </p>
          )}
        </div>

        {/* Next */}
        {hasNext && (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-3 flex size-11 items-center justify-center rounded-full text-cream/40 transition-colors hover:bg-white/10 hover:text-cream"
          >
            <svg
              className="size-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Filmstrip */}
      {images.length > 1 && (
        <div className="shrink-0 border-t border-white/5 bg-black/20 px-4 py-3">
          <div
            ref={filmstripRef}
            className="scrollbar-none flex gap-1.5 overflow-x-auto"
          >
            {images.map((img, i) => (
              <button
                key={img.signed_id}
                ref={i === safeIndex ? activeThumbRef : undefined}
                type="button"
                onClick={() => goTo(i)}
                className={`relative shrink-0 overflow-hidden rounded transition-all duration-150 ${
                  i === safeIndex
                    ? "ring-2 ring-cream/80 ring-offset-1 ring-offset-charcoal"
                    : "opacity-40 hover:opacity-70"
                }`}
              >
                <OptimizedImage
                  src={img.url}
                  alt={`Thumbnail ${i + 1}`}
                  className="h-14 w-20 object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
